// C3 — Programación Semanal de Supervisores (coordinador).
//
// Refactor visual 2026-05-28 alineado a C1/C2 + diseño piloto UPG:
//   - HeaderRibbon con métricas inline + acciones (Copiar / Publicar ceremonial)
//   - Fila de chips de supervisores activos con avatar + turnos esta semana
//   - Grid filas turno (T1/T2/T3) × columnas días, sin destaque de hoy con amber
//   - Celdas con avatar de iniciales + estado (asignada / sin asignar / conflicto)
//   - "Conflicto" = doble turno mismo día (visual, NO bloqueante a nivel BD)
//   - Modal optimizado: lista de cards con load actual + warning de doble turno

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import type {
  CoordinadorProgramacionSupervisoresSemana,
  ProgramacionSupervisorEnriquecida,
  SupervisorResumen,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { ApiError } from '../../lib/api/client';
import {
  useBorrarProgramacionSupervisor,
  useCoordinadorProgramacionSupervisoresSemana,
  useCopiarSemanaAnteriorSupervisores,
  useCrearProgramacionSupervisor,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { textoOmitidasDiaSinTurno } from '../../lib/textoOmitidasDiaSinTurno';
import { C3ImportarSupervisoresDialog } from './C3ImportarSupervisoresDialog';
import { CeldaNoOperaTooltip } from './CeldaNoOperaTooltip';

// =============================================================================
// Helpers
// =============================================================================

const FMT_DIA_SEMANA = new Intl.DateTimeFormat('es-CO', { weekday: 'short', timeZone: 'UTC' });

/** Nombre corto capitalizado del día de una fecha ISO ('2026-06-29' → 'Lun').
 *  Deriva del día REAL, así el grid funciona con cualquier inicio de semana. */
function diaSemanaCorto(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const [y, m, d] = fechaIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const corto = FMT_DIA_SEMANA.format(new Date(Date.UTC(y, m - 1, d))).replace(/\./g, '');
  return corto.charAt(0).toUpperCase() + corto.slice(1);
}

function formatearFechaCorta(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

function formatearFechaLarga(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return '';
  const parts = fechaIso.slice(0, 10).split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return fmt.format(date).replace(/\.|,/g, '').replace(/\s+/g, ' ').trim();
}

function semanaIsoOffset(iso: string, deltaSemanas: number): string {
  const m = iso.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return iso;
  const año = Number(m[1]);
  const semana = Number(m[2]);
  const ene4 = new Date(Date.UTC(año, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - (dow - 1));
  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1 + deltaSemanas) * 7);
  const tmp = new Date(Date.UTC(lunes.getUTCFullYear(), lunes.getUTCMonth(), lunes.getUTCDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const wn = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
}

// =============================================================================
// Componente principal
// =============================================================================

export function C3ProgramacionSupervisoresPage() {
  const { state, signOut } = useAuth();
  const [semanaIso, setSemanaIso] = useState<string | null>(null);
  const { data, isLoading, isError, error } =
    useCoordinadorProgramacionSupervisoresSemana(semanaIso);
  const [modalCelda, setModalCelda] = useState<{
    fecha: string;
    turno_id: string;
  } | null>(null);
  const [importarOpen, setImportarOpen] = useState(false);

  const copiarMut = useCopiarSemanaAnteriorSupervisores();

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando programación…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar la programación</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => signOut()} variant="outline">
          Cerrar sesión
        </Button>
      </main>
    );
  }

  function handleCopiarSemanaAnterior() {
    copiarMut.mutate(
      { semana_iso_destino: data!.semana_iso },
      {
        onSuccess: (r) => {
          toast.success(`Copiadas ${r.filas_creadas} asignaciones desde ${r.semana_iso_origen}`);
          const textoOmitidas = textoOmitidasDiaSinTurno(r.omitidas_dia_sin_turno);
          if (textoOmitidas) toast.info(textoOmitidas);
        },
        onError: (err) => {
          if (err instanceof ApiError) toast.error(err.message);
          else toast.error('Error al copiar');
        },
      },
    );
  }

  function handlePublicar() {
    // Ceremonial — replica patrón de C2 (no introduce campo `estado` en BD).
    toast.success(`Programación de ${data!.semana_iso} lista para captura`);
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <HeaderRibbon
          data={data}
          onSemanaIsoChange={setSemanaIso}
          onCopiarSemanaAnterior={handleCopiarSemanaAnterior}
          onPublicar={handlePublicar}
          onImportar={() => setImportarOpen(true)}
          isCopiando={copiarMut.isPending}
        />

        {data.supervisores.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-fg-tertiary">
              No hay supervisores activos en este tenant. Pídale al admin que cree usuarios con rol
              supervisor.
            </CardContent>
          </Card>
        ) : (
          <>
            <SupervisoresRibbon data={data} />
            <GridSemanal data={data} onCeldaClick={(celda) => setModalCelda(celda)} />
          </>
        )}

        {modalCelda ? (
          <ModalAsignarSupervisor
            data={data}
            celda={modalCelda}
            onClose={() => setModalCelda(null)}
          />
        ) : null}

        {importarOpen ? (
          <C3ImportarSupervisoresDialog data={data} onClose={() => setImportarOpen(false)} />
        ) : null}

        <footer className="text-center text-[11px] text-fg-tertiary">
          C3 · semana {data.semana_iso} · {data.metricas.celdas_asignadas}/
          {data.metricas.celdas_total} celdas asignadas
        </footer>
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// HeaderRibbon — título + navegación + métricas inline + acciones
// =============================================================================

function HeaderRibbon({
  data,
  onSemanaIsoChange,
  onCopiarSemanaAnterior,
  onPublicar,
  onImportar,
  isCopiando,
}: {
  data: CoordinadorProgramacionSupervisoresSemana;
  onSemanaIsoChange: (iso: string | null) => void;
  onCopiarSemanaAnterior: () => void;
  onPublicar: () => void;
  onImportar: () => void;
  isCopiando: boolean;
}) {
  // Primer y último día de la semana OPERATIVA (el backend manda el orden).
  const inicioSemana = data.dias[0]!;
  const finSemana = data.dias[6]!;
  const semanaNum = data.semana_iso.split('-W')[1];
  const plantasLabel = data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ');

  return (
    <header className="flex flex-col gap-3 border-b border-border pb-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-medium text-fg">Programación de supervisores</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {plantasLabel || data.tenant.nombre} · Semana {semanaNum} ·{' '}
            {formatearFechaCorta(inicioSemana)} → {formatearFechaCorta(finSemana)} ·{' '}
            <span className="tabular-nums">{data.metricas.slots_potenciales}</span> celdas
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSemanaIsoChange(semanaIsoOffset(data.semana_iso, -1))}
          >
            ← Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => onSemanaIsoChange(null)}>
            Esta semana
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSemanaIsoChange(semanaIsoOffset(data.semana_iso, 1))}
          >
            Siguiente →
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <MetricaInline
            label="Asignadas"
            value={`${data.metricas.celdas_asignadas} / ${data.metricas.celdas_total}`}
            tone={data.metricas.celdas_asignadas === data.metricas.celdas_total ? 'ok' : 'neutral'}
          />
          <MetricaInline
            label="Conflictos"
            value={String(data.metricas.conflictos_count)}
            tone={data.metricas.conflictos_count > 0 ? 'crit' : 'ok'}
          />
          <MetricaInline
            label="Sin asignar"
            value={String(data.metricas.sin_asignar_count)}
            tone={data.metricas.sin_asignar_count > 0 ? 'warn' : 'ok'}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!data.semana_anterior_tiene_data || isCopiando}
            onClick={onCopiarSemanaAnterior}
          >
            {isCopiando ? 'Copiando…' : 'Copiar semana anterior'}
          </Button>
          <Button variant="outline" size="sm" onClick={onImportar}>
            Importar Excel
          </Button>
          <Button size="sm" onClick={onPublicar}>
            Publicar programación
          </Button>
        </div>
      </div>
    </header>
  );
}

function MetricaInline({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ok' | 'crit' | 'warn' | 'neutral';
}) {
  const toneCls =
    tone === 'ok'
      ? 'text-ok'
      : tone === 'crit'
        ? 'text-crit'
        : tone === 'warn'
          ? 'text-warn'
          : 'text-fg';
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className={cn('text-sm font-medium tabular-nums', toneCls)}>{value}</span>
    </div>
  );
}

// =============================================================================
// SupervisoresRibbon — chips horizontales con avatar + load semanal
// =============================================================================

function SupervisoresRibbon({ data }: { data: CoordinadorProgramacionSupervisoresSemana }) {
  return (
    <ul
      className="flex flex-wrap items-center gap-2"
      aria-label="Supervisores activos esta semana"
      role="list"
    >
      {data.supervisores.map((s) => {
        const turnos = data.turnos_por_supervisor[s.id] ?? 0;
        const dobles = data.dobles_turno_por_supervisor[s.id]?.length ?? 0;
        return (
          <li
            key={s.id}
            className="flex items-center gap-2 rounded-md border border-border bg-surface-base px-2.5 py-1.5"
          >
            <span className="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay text-[11px] font-semibold text-fg">
              {s.iniciales}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[13px] font-medium text-fg">
                {s.nombre} {s.apellido}
              </span>
              <span className="text-[10.5px] text-fg-tertiary">
                <span className="tabular-nums">{turnos}</span> turno{turnos === 1 ? '' : 's'} esta
                semana
                {dobles > 0 ? (
                  <>
                    {' · '}
                    <span className="text-crit">
                      {dobles} doble{dobles === 1 ? '' : 's'}
                    </span>
                  </>
                ) : null}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// =============================================================================
// GridSemanal — filas turno × columnas días
// =============================================================================

function GridSemanal({
  data,
  onCeldaClick,
}: {
  data: CoordinadorProgramacionSupervisoresSemana;
  onCeldaClick: (celda: { fecha: string; turno_id: string }) => void;
}) {
  const byCelda = useMemo(() => {
    const m = new Map<string, ProgramacionSupervisorEnriquecida>();
    for (const p of data.programaciones) {
      m.set(`${p.fecha}|${p.turno_id}`, p);
    }
    return m;
  }, [data.programaciones]);

  // Para "conflicto" en celda: el supervisor de esta celda tiene >1 turno
  // ESE DÍA. Buscamos por (supervisor_user_id, fecha).
  const enConflictoPorCelda = useMemo(() => {
    const set = new Set<string>();
    const cuentas = new Map<string, number>();
    for (const p of data.programaciones) {
      const k = `${p.supervisor_user_id}|${p.fecha}`;
      cuentas.set(k, (cuentas.get(k) ?? 0) + 1);
    }
    for (const p of data.programaciones) {
      const k = `${p.supervisor_user_id}|${p.fecha}`;
      if ((cuentas.get(k) ?? 0) >= 2) {
        set.add(`${p.fecha}|${p.turno_id}`);
      }
    }
    return set;
  }, [data.programaciones]);

  const turnosOrdenados = useMemo(
    () => [...data.turnos].sort((a, b) => a.numero - b.numero),
    [data.turnos],
  );

  // Días "No opera" por (fecha, turno) — design horario-turno-dia §5/§6.
  const noOperaPorCelda = useMemo(() => {
    const set = new Set<string>();
    for (const h of data.horarios_por_fecha) {
      if (h.no_opera) set.add(`${h.fecha}|${h.turno_id}`);
    }
    return set;
  }, [data.horarios_por_fecha]);

  return (
    <Card>
      {/* max-h + overflow-auto: el scroll vertical vive en el contenedor para
          que el thead sticky mantenga los días visibles (mismo patrón que la
          matriz del detalle C4). */}
      <CardContent className="max-h-[calc(100vh-180px)] overflow-auto p-0">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20">
            <tr className="bg-surface-overlay">
              <th className="sticky left-0 z-30 border-b border-r border-border bg-surface-overlay px-3 py-2 text-left">
                <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  Turno
                </span>
              </th>
              {data.dias.map((fecha) => (
                <th
                  key={fecha}
                  className="border-b border-l border-border bg-surface-overlay px-2 py-2 text-center"
                >
                  <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                    {diaSemanaCorto(fecha)} {fecha.slice(8, 10)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {turnosOrdenados.map((t) => (
              <tr key={t.id}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 border-b border-r border-border bg-surface-base px-3 py-2 text-left align-top"
                >
                  <div className="text-sm font-medium text-fg">T{t.numero}</div>
                  <div className="font-mono text-[10.5px] text-fg-tertiary">
                    {t.hora_inicio.slice(0, 5)}–{t.hora_fin.slice(0, 5)}
                  </div>
                </th>
                {data.dias.map((fecha) => {
                  const key = `${fecha}|${t.id}`;
                  const prog = byCelda.get(key);
                  const esPasada = fecha < data.fecha_hoy;
                  const enConflicto = enConflictoPorCelda.has(key);
                  const noOpera = noOperaPorCelda.has(key);
                  return (
                    <td
                      key={key}
                      className={cn(
                        'border-b border-l border-border p-1.5 align-top',
                        esPasada && 'bg-surface-overlay/40',
                      )}
                    >
                      <CeldaSupervisor
                        prog={prog}
                        esPasada={esPasada}
                        enConflicto={enConflicto}
                        noOpera={noOpera}
                        turnoNombre={t.nombre}
                        onClick={() =>
                          !esPasada && !noOpera && onCeldaClick({ fecha, turno_id: t.id })
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function CeldaSupervisor({
  prog,
  esPasada,
  enConflicto,
  noOpera,
  turnoNombre,
  onClick,
}: {
  prog: ProgramacionSupervisorEnriquecida | undefined;
  esPasada: boolean;
  enConflicto: boolean;
  /** Día "No opera" para este (turno, fecha) — design horario-turno-dia §5/§6. */
  noOpera: boolean;
  turnoNombre: string;
  onClick: () => void;
}) {
  if (!prog && noOpera) {
    // Deshabilitada, sin acción: el turno no opera este día (design §6). No
    // aplica a una asignación ya existente (S6b decide qué pasa con una
    // atrapada por un cambio de horario; fuera de este slice).
    return (
      <div
        aria-disabled="true"
        className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border px-2 py-2 text-[11px] text-fg-tertiary/40"
      >
        <span>No opera</span>
        <CeldaNoOperaTooltip turnoNombre={turnoNombre} />
      </div>
    );
  }

  if (!prog) {
    return (
      <button
        type="button"
        disabled={esPasada}
        onClick={onClick}
        className={cn(
          'flex w-full items-center justify-center gap-1 rounded-md border border-dashed px-2 py-2 text-[11px] transition-colors',
          esPasada
            ? 'cursor-not-allowed border-border text-fg-tertiary/40'
            : 'cursor-pointer border-warn/40 text-warn hover:bg-warn-soft/30',
        )}
      >
        {esPasada ? (
          '—'
        ) : (
          <>
            <span aria-hidden>⚠</span>
            <span>Sin asignar</span>
          </>
        )}
      </button>
    );
  }
  const iniciales =
    `${prog.supervisor_nombre[0] ?? ''}${prog.supervisor_apellido[0] ?? ''}`.toUpperCase();
  return (
    <button
      type="button"
      disabled={esPasada}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-1.5 rounded-md border bg-surface-base px-2 py-1.5 text-left transition-colors',
        enConflicto ? 'border-crit text-crit' : 'border-border text-fg hover:border-fg-tertiary',
        esPasada && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'flex size-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
          enConflicto ? 'bg-crit-soft text-crit' : 'bg-surface-overlay text-fg',
        )}
      >
        {iniciales}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[11.5px] font-medium">
          {prog.supervisor_nombre} {prog.supervisor_apellido}
        </span>
        {enConflicto ? (
          <span className="text-[9.5px] uppercase tracking-wider text-crit">Conflicto</span>
        ) : null}
      </span>
      <span aria-hidden className="text-fg-tertiary">
        ⌄
      </span>
    </button>
  );
}

// =============================================================================
// ModalAsignarSupervisor — picker tipo lista de cards (no <Select>)
// =============================================================================

function ModalAsignarSupervisor({
  data,
  celda,
  onClose,
}: {
  data: CoordinadorProgramacionSupervisoresSemana;
  celda: { fecha: string; turno_id: string };
  onClose: () => void;
}) {
  const existente = useMemo(() => {
    return data.programaciones.find(
      (p) => p.fecha === celda.fecha && p.turno_id === celda.turno_id,
    );
  }, [data.programaciones, celda]);

  const turno = data.turnos.find((t) => t.id === celda.turno_id);

  // Supervisores que ya están en algún OTRO turno del mismo día (excluyendo
  // este turno) — para flag "ya está en TX hoy".
  const otrosTurnosHoy = useMemo(() => {
    const m = new Map<string, { turnoNumero: number }>();
    for (const p of data.programaciones) {
      if (p.fecha !== celda.fecha) continue;
      if (p.turno_id === celda.turno_id) continue;
      const turnoP = data.turnos.find((t) => t.id === p.turno_id);
      if (!turnoP) continue;
      m.set(p.supervisor_user_id, { turnoNumero: turnoP.numero });
    }
    return m;
  }, [data.programaciones, data.turnos, celda]);

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string>(existente?.supervisor_user_id ?? '');

  const supervisoresFiltrados = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return data.supervisores;
    return data.supervisores.filter(
      (sup) =>
        sup.nombre.toLowerCase().includes(s) ||
        sup.apellido.toLowerCase().includes(s) ||
        sup.email.toLowerCase().includes(s),
    );
  }, [data.supervisores, search]);

  const crear = useCrearProgramacionSupervisor();
  const borrar = useBorrarProgramacionSupervisor();
  const isBusy = crear.isPending || borrar.isPending;

  async function handleGuardar() {
    if (!selectedId) {
      toast.error('Elija un supervisor');
      return;
    }
    if (existente && selectedId === existente.supervisor_user_id) {
      onClose();
      return;
    }
    try {
      if (existente) await borrar.mutateAsync(existente.id);
      await crear.mutateAsync({
        fecha: celda.fecha,
        turno_id: celda.turno_id,
        supervisor_user_id: selectedId,
      });
      toast.success('Programación guardada');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message);
      else toast.error('Error al guardar');
    }
  }

  async function handleBorrarExistente() {
    if (!existente) return;
    try {
      await borrar.mutateAsync(existente.id);
      toast.success('Programación borrada');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message);
      else toast.error('Error al borrar');
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existente ? 'Cambiar supervisor' : 'Asignar supervisor'}</DialogTitle>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {formatearFechaLarga(celda.fecha)} ·{' '}
            <span className="font-mono">
              T{turno?.numero ?? '?'} · {turno?.hora_inicio.slice(0, 5) ?? '--'}–
              {turno?.hora_fin.slice(0, 5) ?? '--'}
            </span>
          </p>
        </DialogHeader>

        {existente ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-overlay/50 px-2.5 py-2">
            <div className="flex items-center gap-2">
              <SupervisorAvatar
                supervisor={data.supervisores.find((s) => s.id === existente.supervisor_user_id)}
                size={7}
              />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-fg-tertiary">
                  Asignado actualmente
                </div>
                <div className="text-[13px] font-medium text-fg">
                  {existente.supervisor_nombre} {existente.supervisor_apellido}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleBorrarExistente} disabled={isBusy}>
              Borrar
            </Button>
          </div>
        ) : null}

        <Input
          placeholder="Buscar supervisor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <ul className="flex max-h-[300px] flex-col gap-1.5 overflow-y-auto pr-1" role="list">
          {supervisoresFiltrados.length === 0 ? (
            <li className="py-4 text-center text-[12px] text-fg-tertiary">Sin resultados</li>
          ) : (
            supervisoresFiltrados.map((sup) => {
              const turnos = data.turnos_por_supervisor[sup.id] ?? 0;
              const otroTurno = otrosTurnosHoy.get(sup.id);
              const isSelected = selectedId === sup.id;
              const isExistente = existente?.supervisor_user_id === sup.id;
              return (
                <li key={sup.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(sup.id)}
                    onDoubleClick={() => {
                      setSelectedId(sup.id);
                      void handleGuardar();
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors',
                      isSelected
                        ? 'border-accent bg-accent-soft/40'
                        : 'border-border bg-surface-base hover:border-fg-tertiary',
                    )}
                  >
                    <SupervisorAvatar supervisor={sup} size={8} />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-[13px] font-medium text-fg">
                          {sup.nombre} {sup.apellido}
                        </span>
                        {isExistente ? (
                          <Badge tone="neutral" className="text-[9px]">
                            actual
                          </Badge>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-baseline gap-1.5 text-[10.5px] text-fg-tertiary">
                        <span>
                          <span className="tabular-nums">{turnos}</span> turno
                          {turnos === 1 ? '' : 's'} esta semana
                        </span>
                        {otroTurno ? (
                          <span className="text-warn">
                            · ⚠ ya está en T{otroTurno.turnoNumero} hoy (doble turno)
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {isSelected ? (
                      <span aria-hidden className="text-accent">
                        ✓
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={isBusy || !selectedId}>
            {isBusy ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupervisorAvatar({
  supervisor,
  size = 8,
}: {
  supervisor: SupervisorResumen | undefined;
  size?: 6 | 7 | 8;
}) {
  const sizeCls = size === 6 ? 'size-6' : size === 7 ? 'size-7' : 'size-8';
  const textCls = size === 6 ? 'text-[10px]' : 'text-[11px]';
  return (
    <span
      className={cn(
        'flex flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay font-semibold text-fg',
        sizeCls,
        textCls,
      )}
    >
      {supervisor?.iniciales ?? '·'}
    </span>
  );
}
